import { memo } from 'react';

import { TradeMode } from '@flipster/apis/v2/trade/types';
import { fireYourPositionsManageClickEvent } from '@flipster/features/src/position-close-modal/segment';
import { TpslButton, type TpslIdentity } from '@flipster/features/src/shared/tpsl/tpsl-button';
import { TableCell } from '@flipster/ui/Table';
import { OrderMarginType, V2OrderSideType } from '@flipster/utils/types/base';

import { AirdropTpSlCell } from './airdrop-tpsl-cell';

// identity(symbol/positionType/parentId)는 호출부가 primitive로 내려준다.
// position 객체를 통째로 받지 않으므로 열린 포지션 셀이 pnl tick마다 리렌더되지 않는다.
interface Props extends TpslIdentity {
  tpSlType: 'tp' | 'sl';
  price: string | null;
  precision: number;
  disabled?: boolean;
  isReduceOnly: boolean;
  isAirdrop?: boolean;
  marginType?: OrderMarginType;
  /**
   * manage(Set) 클릭 시 실행할 콜백. 넘기면 아래 analytics-입력 props 대신 이 콜백이 쓰인다.
   * 열린 포지션 셀은 pnl 등 변동 값을 매 tick prop으로 구독하지 않고, 이 콜백에서 클릭 시점에 읽어 발사한다.
   */
  onManageClick?: () => void;
  // --- 기본(fallback) analytics 입력. onManageClick을 넘기지 않는 호출부(pending 행)에서만 사용 ---
  side?: V2OrderSideType;
  initLeverage?: number;
  unrealizedPnl?: string;
  calcUnrealizedPnlPercent?: string;
  fundsInvested?: string;
  tradeMode?: TradeMode;
  showAllSymbol?: boolean;
  isProfitBoostMode?: boolean;
}

const TpSlCol = memo(
  ({
    symbol,
    positionType,
    parentId,
    side,
    tpSlType,
    price,
    precision,
    disabled,
    initLeverage,
    unrealizedPnl,
    calcUnrealizedPnlPercent,
    fundsInvested,
    tradeMode,
    showAllSymbol,
    isProfitBoostMode,
    isAirdrop,
    marginType,
    isReduceOnly,
    onManageClick,
  }: Props) => {
    if (isAirdrop) return <AirdropTpSlCell price={price} precision={precision} showInfo={tpSlType === 'tp'} />;
    if (isReduceOnly) return <TableCell>-</TableCell>;
    return (
      <TableCell>
        <TpslButton
          symbol={symbol}
          positionType={positionType}
          parentId={parentId}
          tpslType={tpSlType}
          marginType={marginType}
          disabled={disabled}
          onClick={
            onManageClick ??
            (() => {
              if (!tradeMode || !symbol || !side || fundsInvested === undefined) return;
              fireYourPositionsManageClickEvent(tpSlType === 'tp' ? 'Take profit' : 'Stop loss', {
                symbol,
                side,
                initLeverage,
                unrealizedPnl,
                calcUnrealizedPnlPercent,
                fundsInvested,
                mode: tradeMode,
                isShowAll: showAllSymbol ?? false,
                isProfitBoostMode: Boolean(isProfitBoostMode),
              });
            })
          }
        />
      </TableCell>
    );
  },
);

export default TpSlCol;
